import { useState, ClipboardEvent, useEffect, useRef } from 'react'
import * as React from 'react'
import { FloatingPopover } from 'components/FloatingPopover/FloatingPopover'
import { Formik, Form, Field, FormikHelpers, FieldProps } from 'formik'
import { HandbookItem, LinkHandbookItem } from 'types'
import { isEmpty } from 'lodash'
import Foco from 'react-foco'
import cx from 'classnames'
import { Link as LinkIcon } from 'almanac-ui/icons'
import { HandbookItemParams } from 'api/clients/HandbookClient'
import { Button } from 'components/Button'
import { useHandbookItems } from '../HandbookItemProvider'
import { getURLMetadata } from './getURLMetadata'
import { useBlurOnNavigateRef } from './hooks'
import { validUrlRegexp } from 'regexps'
import Styles from '../HandbookItems.module.scss'
import { NavItem } from 'components/NavItem'
import { LinkItemMenu } from 'components/Handbook2_0/ContextMenus/ItemMenu/LinkItemMenu'
import { MenuType } from 'components/Handbook2_0/ContextMenus/Types'
import { useHandbookPermissions } from 'components/Handbook2_0/hooks'
import { observer } from 'mobx-react-lite'

interface Props {
  item: LinkHandbookItem
  depth?: number
}

const addHttpProtocol = (url: string | undefined) => (url?.startsWith('http') ? url : 'https://' + url)

const isValidUrl = (url: string) => {
  return !!url.match(validUrlRegexp)
}

export const Link = observer((props: Props) => {
  const { item, depth = 0 } = props
  const { currentHandbook: handbook, updateItem } = useHandbookItems()
  const { canEdit } = useHandbookPermissions(handbook)
  const [isEditing, setIsEditing] = useState(false)

  if (isEditing) {
    return (
      <div className={Styles.renameWrapper}>
        <LinkForm
          onSubmit={async (values) => {
            await updateItem(item, values)
            setIsEditing(false)
          }}
          onCancel={() => setIsEditing(false)}
          title={item.title}
          url={item.url}
          favicon={item.favicon}
        />
      </div>
    )
  }

  if (!item.url) return null

  return (
    <LinkItemMenu
      item={item}
      currentUserCanAccess={canEdit}
      type={MenuType.contextMenu}
      onEdit={() => setIsEditing(true)}
    >
      <div>
        <NavItem.Root indentLevel={depth} elementType="a" to={item.url} target="_blank">
          <NavItem.Emoji>
            {item.favicon ? (
              <div className={Styles.linkIconWrap}>
                <img className={cx(Styles.icon, Styles.link)} src={item.favicon} alt={item.title} />
              </div>
            ) : (
              <LinkIcon />
            )}
          </NavItem.Emoji>
          <NavItem.Label label={item.title || ''} />
          <NavItem.ControlGroup>
            <LinkItemMenu
              item={item}
              type={MenuType.popover}
              currentUserCanAccess={canEdit}
              children={undefined}
              onEdit={() => setIsEditing(true)}
            />
          </NavItem.ControlGroup>
        </NavItem.Root>
      </div>
    </LinkItemMenu>
  )
})

interface LinkFormProps {
  title?: string
  url?: string
  favicon?: string
  onSubmit: (values: Pick<HandbookItem, 'title' | 'url' | 'favicon'>) => void
  onCancel: () => void
}

export const LinkForm = ({ url, title, favicon, onSubmit, onCancel }: LinkFormProps) => {
  const inputEl = useBlurOnNavigateRef<HTMLInputElement>()
  const [showForm, setShowForm] = useState(Boolean(title || url))
  const { currentHandbook: handbook } = useHandbookItems()
  const inputWrapperRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const handleFocusOutside = (e: FocusEvent) => {
    // need to wait 1 tick of event loop so the <form /> element is created when we check the target
    setTimeout(() => {
      const onFormOrInput = [inputWrapperRef, formRef].some((ref) => ref.current?.contains(e.target as Node))
      if (!onFormOrInput) {
        onCancel()
      }
    }, 0)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!handbook) return null

  const syncPageMeta = async (
    url: string | undefined,
    setFieldValue: FormikHelpers<HandbookItemParams>['setFieldValue'],
  ) => {
    const fullUrl = addHttpProtocol(url)
    if (url && isValidUrl(fullUrl)) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const meta = await getURLMetadata(fullUrl!)
      setFieldValue('url', fullUrl)
      setShowForm(true)

      if (meta && 'links' in meta) {
        setFieldValue('title', meta.meta.title)
        setFieldValue('favicon', meta.links.icon?.[0]?.href)
      }
    }
  }

  return (
    <Formik<HandbookItemParams>
      onSubmit={(values) => onSubmit({ ...values, url: addHttpProtocol(values.url) })}
      initialValues={{ url, title, favicon }}
      validate={(values) => {
        const errors = {} as { url?: boolean; title?: boolean }
        if (!values.url || !isValidUrl(addHttpProtocol(values.url))) {
          errors.url = true
        }
        if (isEmpty(values.title)) {
          errors.title = true
        }
        return errors
      }}
    >
      {({ values, setFieldValue, isValid, isSubmitting, errors, touched }) => (
        <FloatingPopover
          tooltipState={{ isOpen: showForm }}
          placement="right"
          content={
            <Foco onFocusOutside={handleFocusOutside}>
              <Form ref={formRef}>
                <div className={Styles.popoverContent}>
                  <fieldset>
                    <label htmlFor="url">URL</label>

                    <div className={cx(Styles.inputWrapper, { [Styles.error]: !isValid })}>
                      <Field name="url" placeholder="Paste or type an URL" autoFocus={!values.url || !!values.title} />
                    </div>
                  </fieldset>

                  <fieldset>
                    <label htmlFor="title">Display Text</label>

                    <div className={cx(Styles.inputWrapper, { [Styles.error]: !!errors.title && touched?.title })}>
                      <Field
                        name="title"
                        placeholder="What do you want this link to be called?"
                        autoFocus={!!values.url && !values.title}
                      />
                    </div>
                  </fieldset>

                  <hr className={Styles.divider} />

                  <div className={Styles.buttons}>
                    <Button size="lg" variant="secondaryGrey" onPress={onCancel}>
                      Cancel
                    </Button>
                    <Button size="lg" type="submit" isDisabled={!isValid || isSubmitting}>
                      Add Link
                    </Button>
                  </div>
                </div>
              </Form>
            </Foco>
          }
        >
          <Foco onFocusOutside={handleFocusOutside}>
            <div className={cx(Styles.inputWrapper, { [Styles.error]: !!errors.url })} ref={inputWrapperRef}>
              <LinkIcon className={cx(Styles.icon, Styles.link)} viewBox="-2 -2 20 20" />
              <Field name="url">
                {({ field }: FieldProps) => (
                  <input
                    ref={inputEl}
                    {...field}
                    autoFocus={!showForm}
                    placeholder={values.title && !values.url ? values.title : 'Paste a URL'}
                    onPaste={(event: ClipboardEvent<HTMLInputElement>) => {
                      return syncPageMeta(event.clipboardData?.getData('text'), setFieldValue)
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      switch (e.key) {
                        case 'Enter':
                          e.preventDefault()
                          return syncPageMeta(e.currentTarget.value, setFieldValue)
                        case 'Escape':
                          onCancel()
                      }
                    }}
                    onBlur={(e) => {
                      field.value && !errors?.url ? syncPageMeta(values.url, setFieldValue) : onCancel()
                      field.onBlur(e)
                    }}
                  />
                )}
              </Field>
            </div>
          </Foco>
        </FloatingPopover>
      )}
    </Formik>
  )
}
